import { routePath } from "config/routeMgt/routePath";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { talentProfileStore } from "./TalentProfileStore";

interface MenuItem {
  label: string;
  link: string;
  required?: boolean;
}

interface TalentProfileSidebarProps {
  active?: string;
  onSelect?: (item: string) => void;
}

type SectionStatusType = {
  "Basic Details": boolean;
  Education: boolean;
  Experience: boolean;
};

const menuItems: MenuItem[] = [
  { label: "Basic Details", link: routePath.settings, required: true },
  { label: "Education", link: routePath.education, required: true },
  { label: "Experience", link: routePath.experience, required: true },
  { label: "URLs", link: routePath.urls, required: false },
  { label: "Interests", link: routePath.interests, required: false },
  { label: "Certifications", link: routePath.certifications, required: false },
  { label: "Projects", link: routePath.projects, required: false },
];

const TalentProfileSidebar: React.FC<TalentProfileSidebarProps> = ({
  active,
  onSelect,
}) => {
  const location = useLocation();
  const [sectionStatus, setSectionStatus] = useState<SectionStatusType>(
    talentProfileStore.getStatus(),
  );
  const [isStoreReady, setIsStoreReady] = useState(false);

  useEffect(() => {
    const currentStatus = talentProfileStore.getStatus();
    setSectionStatus(currentStatus);

    const hasData = Object.values(currentStatus).some(
      (value) => value === true,
    );

    if (hasData) {
      setIsStoreReady(true);
    } else {
      setIsStoreReady(false);
    }

    const unsubscribe = talentProfileStore.subscribe(
      (status: SectionStatusType) => {
            console.log("📢 Sidebar received store update:", status);
        setSectionStatus(status);
        setIsStoreReady(true);
      },
    );

    return unsubscribe;
  }, []);

  const getStatusByLabel = (label: string): boolean => {
    switch (label) {
      case "Basic Details":
        return sectionStatus["Basic Details"];
      case "Education":
        return sectionStatus.Education;
      case "Experience":
        return sectionStatus.Experience;
      default:
        return false;
    }
  };

  return (
    <div className="md:col-span-1">
      <div className="border-2 p-4 rounded border-white shadow-lg">
        {menuItems.map((item) => {
          const isActive =
            active === item.label || location.pathname === item.link;
          const isCompleted = getStatusByLabel(item.label);
          const showRequiredBadge =
            isStoreReady && item.required && !isCompleted;

          return (
            <Link
              key={item.label}
              to={item.link}
              onClick={() => onSelect?.(item.label)}
            >
              <div
                className={`cursor-pointer p-1 rounded mb-3 
                ${isActive ? "bg-blue-200 text-blue-900 font-semibold" : "hover:bg-blue-50"}`}
              >
                <span className="flex flex-wrap items-center gap-1">
                  {item.label}
                  {showRequiredBadge && (
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <span className="text-red-500 text-sm">(Required)</span>
                      <span
                        className="text-red-500 cursor-help"
                        title="This section is required and must be completed"
                      >
                        <svg
                          className="inline-block w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </span>
                  )}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TalentProfileSidebar;
